import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { volunteersApi } from '../volunteers';
import type {
  CreateVolunteerDto,
  UpdateVolunteerDto,
  VolunteerQueryParams,
} from '../../model/types/volunteers';

// Query keys
export const volunteerKeys = {
  all: ['volunteers'] as const,
  lists: () => [...volunteerKeys.all, 'list'] as const,
  list: (recitalId: string, params?: VolunteerQueryParams) =>
    [...volunteerKeys.lists(), recitalId, params] as const,
  details: () => [...volunteerKeys.all, 'detail'] as const,
  detail: (volunteerId: string) =>
    [...volunteerKeys.details(), volunteerId] as const,
  mySignups: (recitalId: string) =>
    [...volunteerKeys.all, 'my-signups', recitalId] as const,
};

// Hooks

// Get all volunteers for a recital
export const useVolunteers = (
  recitalId: string,
  params?: VolunteerQueryParams
) => {
  return useQuery({
    queryKey: volunteerKeys.list(recitalId, params),
    queryFn: () => volunteersApi.list(recitalId, params),
    enabled: !!recitalId,
  });
};

// Get volunteer details
export const useVolunteer = (volunteerId: string) => {
  return useQuery({
    queryKey: volunteerKeys.detail(volunteerId),
    queryFn: () => volunteersApi.getOne(volunteerId),
    enabled: !!volunteerId,
  });
};

// Get parent's own signups
export const useMyVolunteerSignups = (recitalId: string) => {
  return useQuery({
    queryKey: volunteerKeys.mySignups(recitalId),
    queryFn: () => volunteersApi.getMySignups(recitalId),
    enabled: !!recitalId,
  });
};

// Create volunteer signup
export const useCreateVolunteer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      recitalId,
      data,
    }: {
      recitalId: string;
      data: CreateVolunteerDto;
    }) => volunteersApi.create(recitalId, data),
    onSuccess: (_, variables) => {
      // Invalidate and refetch volunteer lists
      queryClient.invalidateQueries({
        queryKey: volunteerKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: volunteerKeys.mySignups(variables.recitalId),
      });
    },
  });
};

// Update volunteer
export const useUpdateVolunteer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      volunteerId,
      data,
    }: {
      volunteerId: string;
      data: UpdateVolunteerDto;
    }) => volunteersApi.update(volunteerId, data),
    onSuccess: (_, variables) => {
      // Invalidate volunteer detail and lists
      queryClient.invalidateQueries({
        queryKey: volunteerKeys.detail(variables.volunteerId),
      });
      queryClient.invalidateQueries({
        queryKey: volunteerKeys.lists(),
      });
    },
  });
};

// Delete volunteer
export const useDeleteVolunteer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (volunteerId: string) => volunteersApi.delete(volunteerId),
    onSuccess: () => {
      // Invalidate all volunteer queries
      queryClient.invalidateQueries({
        queryKey: volunteerKeys.all,
      });
    },
  });
};
